/**
 * ImageViewer Component
 * Full-screen image viewer with zoom, navigation, and controls
 *
 * Features:
 * - Full-screen overlay with semi-transparent background
 * - Keyboard navigation (ESC, Arrow Left/Right)
 * - Mouse controls (click background to close)
 * - Zoom in/out functionality
 * - Next/Previous navigation for multiple images
 * - Download and Share as Quoted Repost (preserves NSFW tags)
 */

import { ToastService } from '../../services/ToastService';
import { downloadMedia } from '../../helpers/downloadMedia';

export interface ImageViewerOptions {
  images: string[]; // Array of image URLs
  initialIndex?: number; // Which image to show first (default: 0)
  sourceEvent?: {
    eventId: string;
    authorPubkey: string;
    isNSFW: boolean;
  };
}

export class ImageViewer {
  private container: HTMLElement | null = null;
  private images: string[] = [];
  private currentIndex: number = 0;
  private zoomLevel: number = 1;
  private isDragging: boolean = false;
  private dragStart = { x: 0, y: 0 };
  private imagePosition = { x: 0, y: 0 };
  private sourceEvent: { eventId: string; authorPubkey: string; isNSFW: boolean } | undefined;

  // Bound event handlers for proper cleanup
  private boundMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundMouseUp: (() => void) | null = null;

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  /**
   * Open the image viewer
   */
  public open(options: ImageViewerOptions): void {
    this.images = options.images;
    this.currentIndex = options.initialIndex || 0;
    this.zoomLevel = 1;
    this.imagePosition = { x: 0, y: 0 };
    this.sourceEvent = options.sourceEvent;

    this.render();
    this.attachEventListeners();
  }

  /**
   * Close the image viewer
   */
  public close(): void {
    this.detachEventListeners();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  /**
   * Navigate to image at given index, resetting zoom state
   */
  private navigateTo(index: number): void {
    if (index < 0 || index >= this.images.length || index === this.currentIndex) return;
    this.currentIndex = index;
    this.zoomLevel = 1;
    this.imagePosition = { x: 0, y: 0 };
    this.updateImage();
  }

  private next(): void {
    this.navigateTo(this.currentIndex + 1);
  }

  private previous(): void {
    this.navigateTo(this.currentIndex - 1);
  }

  /**
   * Zoom in
   */
  private zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 0.5, 5); // Max 5x zoom
    this.updateImage();
  }

  /**
   * Zoom out
   */
  private zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 0.5, 1); // Min 1x zoom
    this.imagePosition = { x: 0, y: 0 }; // Reset position when zooming out
    this.updateImage();
  }

  /**
   * Reset zoom to 1x
   */
  private resetZoom(): void {
    this.zoomLevel = 1;
    this.imagePosition = { x: 0, y: 0 };
    this.updateImage();
  }

  /**
   * Download current image
   */
  private async download(): Promise<void> {
    const imageUrl = this.images[this.currentIndex];
    if (!imageUrl) return;
    const defaultFileName = imageUrl.split('/').pop() || 'image.jpg';
    try {
      await downloadMedia(imageUrl, defaultFileName);
    } catch {
      ToastService.show('Failed to save image', 'error');
    }
  }

  /**
   * Share image as Quoted Repost
   * Extracts current image URL and creates a new note with:
   * - The image itself (URL)
   * - Attribution: "Image via nostr:npub..." (user mention)
   * - Source link to original event
   * - User can add their own text above
   * Preserves NSFW tags for security (NIP-36)
   */
  private async share(): Promise<void> {
    if (!this.sourceEvent) {
      ToastService.show('Cannot share: Image source not available', 'error');
      return;
    }

    try {
      const { encodeNpub } = await import('../../services/NostrToolsAdapter');
      const { PostNoteModal } = await import('../post/PostNoteModal');

      // Get current image URL
      const currentImageUrl = this.images[this.currentIndex];

      // Convert author pubkey to npub mention (nostr:npub format)
      const npub = encodeNpub(this.sourceEvent.authorPubkey);
      const mentionTag = `nostr:${npub}`;

      // Generate nostr.ae URL for the original event
      const sourceUrl = `https://nostr.ae/${this.sourceEvent.eventId}`;

      // Build content: Image URL + attribution with user mention + source link
      // Format: <image-url>\nImage via nostr:npub... https://nostr.ae/...
      let content = '';
      if (this.sourceEvent.isNSFW) {
        content += '⚠️ NSFW Content\n\n';
      }
      content += `${currentImageUrl}\nImage via ${mentionTag} ${sourceUrl}`;

      // Open PostNoteModal with pre-filled content
      PostNoteModal.getInstance().show(content);

      // Close ImageViewer after opening editor
      this.close();
    } catch (error) {
      console.error('Failed to share image:', error);
      ToastService.show('Failed to open share editor', 'error');
    }
  }

  /**
   * Render the viewer
   */
  private render(): void {
    this.container = document.createElement('div');
    this.container.className = 'image-viewer';
    this.container.innerHTML = `
      <div class="image-viewer__background"></div>
      <div class="image-viewer__content">
        <div class="image-viewer__controls">
          <button class="btn btn--square-lg image-viewer__btn--zoom-out" title="Zoom Out" aria-label="Zoom Out">
            <svg width="20" height="20"><use href="#icon-zoom-out"/></svg>
          </button>
          <button class="btn btn--square-lg image-viewer__btn--zoom-in" title="Zoom In" aria-label="Zoom In">
            <svg width="20" height="20"><use href="#icon-zoom-in"/></svg>
          </button>
          <button class="btn btn--square-lg image-viewer__btn--reset" title="Reset Zoom" aria-label="Reset Zoom">
            <svg width="20" height="20"><use href="#icon-reset-zoom"/></svg>
          </button>
          <button class="btn btn--square-lg image-viewer__btn--download" title="Download" aria-label="Download">
            <svg width="20" height="20"><use href="#icon-download"/></svg>
          </button>
          <button class="btn btn--square-lg image-viewer__btn--share" title="Share as Quote" aria-label="Share">
            <svg width="20" height="20"><use href="#icon-share"/></svg>
          </button>
          <button class="btn btn--square-lg image-viewer__btn--close" title="Close (ESC)" aria-label="Close">
            <svg width="20" height="20"><use href="#icon-close"/></svg>
          </button>
        </div>
        <div class="image-viewer__image-container">
          <img class="image-viewer__image" src="${this.images[this.currentIndex]}" alt="Full screen image">
        </div>
        ${this.images.length > 1 ? this.renderNavigation() : ''}
      </div>
    `;

    document.body.appendChild(this.container);

    // Attach button event listeners
    this.attachControlListeners();
    this.attachImageDragListeners();
  }

  /**
   * Render navigation buttons (only if multiple images)
   */
  private renderNavigation(): string {
    return `
      <button class="image-viewer__nav image-viewer__nav--prev ${this.currentIndex === 0 ? 'disabled' : ''}"
              aria-label="Previous image"
              ${this.currentIndex === 0 ? 'disabled' : ''}>
        <svg width="24" height="24"><use href="#icon-chevron-left"/></svg>
      </button>
      <button class="image-viewer__nav image-viewer__nav--next ${this.currentIndex === this.images.length - 1 ? 'disabled' : ''}"
              aria-label="Next image"
              ${this.currentIndex === this.images.length - 1 ? 'disabled' : ''}>
        <svg width="24" height="24"><use href="#icon-chevron-right"/></svg>
      </button>
      <div class="image-viewer__counter">${this.currentIndex + 1} / ${this.images.length}</div>
    `;
  }

  /**
   * Update image and navigation state
   */
  private updateImage(): void {
    if (!this.container) return;

    const img = this.container.querySelector('.image-viewer__image') as HTMLImageElement;
    const currentSrc = this.images[this.currentIndex];
    if (img && currentSrc) {
      img.src = currentSrc;
      img.style.transform = `scale(${this.zoomLevel}) translate(${this.imagePosition.x}px, ${this.imagePosition.y}px)`;
    }

    // Update navigation if multiple images
    if (this.images.length > 1) {
      const prevBtn = this.container.querySelector('.image-viewer__nav--prev') as HTMLButtonElement;
      const nextBtn = this.container.querySelector('.image-viewer__nav--next') as HTMLButtonElement;
      const counter = this.container.querySelector('.image-viewer__counter');

      if (prevBtn) {
        prevBtn.disabled = this.currentIndex === 0;
        prevBtn.classList.toggle('disabled', this.currentIndex === 0);
      }

      if (nextBtn) {
        nextBtn.disabled = this.currentIndex === this.images.length - 1;
        nextBtn.classList.toggle('disabled', this.currentIndex === this.images.length - 1);
      }

      if (counter) {
        counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
      }
    }
  }

  /**
   * Attach control button listeners
   */
  private attachControlListeners(): void {
    if (!this.container) return;

    const zoomInBtn = this.container.querySelector('.image-viewer__btn--zoom-in');
    const zoomOutBtn = this.container.querySelector('.image-viewer__btn--zoom-out');
    const resetBtn = this.container.querySelector('.image-viewer__btn--reset');
    const downloadBtn = this.container.querySelector('.image-viewer__btn--download');
    const shareBtn = this.container.querySelector('.image-viewer__btn--share');
    const closeBtn = this.container.querySelector('.image-viewer__btn--close');

    zoomInBtn?.addEventListener('click', () => this.zoomIn());
    zoomOutBtn?.addEventListener('click', () => this.zoomOut());
    resetBtn?.addEventListener('click', () => this.resetZoom());
    downloadBtn?.addEventListener('click', () => this.download());
    shareBtn?.addEventListener('click', () => this.share());
    closeBtn?.addEventListener('click', () => this.close());

    // Navigation buttons
    const prevBtn = this.container.querySelector('.image-viewer__nav--prev');
    const nextBtn = this.container.querySelector('.image-viewer__nav--next');

    prevBtn?.addEventListener('click', () => this.previous());
    nextBtn?.addEventListener('click', () => this.next());
  }

  /**
   * Attach image drag listeners for panning when zoomed
   */
  private attachImageDragListeners(): void {
    if (!this.container) return;

    const img = this.container.querySelector('.image-viewer__image') as HTMLImageElement;
    if (!img) return;

    img.addEventListener('mousedown', (e: MouseEvent) => {
      if (this.zoomLevel > 1) {
        this.isDragging = true;
        this.dragStart = {
          x: e.clientX - this.imagePosition.x,
          y: e.clientY - this.imagePosition.y
        };
        img.style.cursor = 'grabbing';
      }
    });

    this.boundMouseMove = (e: MouseEvent) => {
      if (this.isDragging && this.zoomLevel > 1) {
        this.imagePosition = {
          x: e.clientX - this.dragStart.x,
          y: e.clientY - this.dragStart.y
        };
        this.updateImage();
      }
    };

    this.boundMouseUp = () => {
      if (this.isDragging) {
        this.isDragging = false;
        if (img) {
          img.style.cursor = this.zoomLevel > 1 ? 'grab' : 'default';
        }
      }
    };

    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  /**
   * Attach global event listeners
   */
  private attachEventListeners(): void {
    document.addEventListener('keydown', this.handleKeyDown);

    // Click on content area (but not on image or controls) closes viewer
    const content = this.container?.querySelector('.image-viewer__content');
    content?.addEventListener('click', (e: Event) => {
      if (e.target === content) {
        this.close();
      }
    });
  }

  /**
   * Detach global event listeners
   */
  private detachEventListeners(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = null;
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
      this.boundMouseUp = null;
    }
  }

  /**
   * Handle keyboard shortcuts
   */
  private handleKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.previous();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  }

}

// Singleton instance
let imageViewerInstance: ImageViewer | null = null;

/**
 * Get or create ImageViewer instance
 */
export function getImageViewer(): ImageViewer {
  if (!imageViewerInstance) {
    imageViewerInstance = new ImageViewer();
  }
  return imageViewerInstance;
}
